import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import {
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  DialogActions,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import MuiToggleButtonGroup, {
  toggleButtonGroupClasses,
} from "@mui/material/ToggleButtonGroup";
import MuiToggleButton, {
  toggleButtonClasses,
} from "@mui/material/ToggleButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { updateSellerTerms } from "src/api/seller";

import font from "src/styles/font";
import { hexToRgba } from "src/common/color";

import Dialog from "src/components/core/Dialog";
import CoreTextField from "src/components/core/TextField";
import Button from "src/components/core/Button";
import { Black, GreyStroke, Primary, White01, White } from "src/styles/color";

const ToggleButtonGroup = muiStyled(MuiToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    borderRadius: 8,
    color: `${Black}`,
    border: `1px solid ${GreyStroke}`,
  },
}));

const ToggleButton = muiStyled(MuiToggleButton)(({ theme }) => ({
  [`&.${toggleButtonClasses.root}:hover`]: {
    color: `${Primary}`,
  },
  [`&.${toggleButtonClasses.root}.Mui-selected`]: {
    background: `${hexToRgba(Primary, 0.2)}`,
  },
}));

const TextField = styled(CoreTextField)`
  &.MuiTextField-root .MuiInputBase-input {
    padding: 10px 12px;
    background-color: ${White};
    border-radius: 16px;
  }
`;

const SetTermContractFeeDialog = ({ userData, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const initialState = {
    multiple_fee: true,
    percent_fee: [{ percent: "10%" }],
    percent_card: "3%",
    language: "th",
  };
  const [contractPercent, setContractPercent] = useState(initialState);

  useEffect(() => {
    if (!userData) return;

    setContractPercent({
      multiple_fee:
        userData?.terms_contract?.percent_fee.length > 1 ? true : false,
      percent_fee: userData?.terms_contract?.percent_fee || [
        { percent: "10%" },
      ],
      percent_card: userData?.terms_contract?.percent_card || "3%",
      language: userData?.terms_contract?.language
        ? userData?.terms_contract?.language
        : "th",
    });
  }, [userData]);

  const handlePercentFee = (action, index) => {
    if (action === "add") {
      const newPercentDesc = contractPercent?.percent_fee || [];
      if (newPercentDesc.length < 6) {
        newPercentDesc.push({ description: "", percent: "" });
        setContractPercent({
          ...contractPercent,
          percent_fee: newPercentDesc,
        });
      }
    } else if (action === "remove") {
      const newPercentDesc = [...contractPercent?.percent_fee];
      newPercentDesc.splice(index, 1);

      if (newPercentDesc.length < 2) {
        setContractPercent({
          ...contractPercent,
          multiple_fee: false,
          percent_fee: [{ percent: newPercentDesc[0]?.percent || "" }],
        });
      } else {
        setContractPercent({
          ...contractPercent,
          percent_fee: newPercentDesc,
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await updateSellerTerms(userData?._id, contractPercent);

      enqueueSnackbar("อัปเดตค่าธรรมเนียมสัญญาสำเร็จ", {
        variant: "success",
      });

      setContractPercent(initialState);
      onSuccess();
    } catch (error) {
      console.error("Error updating contract fee:", error);
      enqueueSnackbar("เกิดข้อผิดพลาดในการอัปเดตค่าธรรมเนียมสัญญา", {
        variant: "error",
      });
    }
  };

  const handleOnClose = () => {
    setContractPercent(initialState);
    onClose();
  };

  const isValid =
    contractPercent?.percent_card &&
    contractPercent?.percent_fee.every((fee) => {
      if (contractPercent?.multiple_fee) {
        return fee.description && fee.percent;
      }
      return fee.percent;
    });

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: 900,
            borderRadius: 4,
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
          px: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        ตั้งค่าค่าธรรมเนียมสัญญา (Contract Fee)
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={6}>
            <Typography variant="subtitle1" sx={{ mb: 0 }}>
              เปอร์เซ็นต์ค่าธรรมเนียมบัตรเครดิต
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              variant="outlined"
              required
              value={contractPercent?.percent_card || ""}
              onChange={(e) =>
                setContractPercent({
                  ...contractPercent,
                  percent_card: e.target.value,
                })
              }
              error={!contractPercent?.percent_card}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle1">
              เปอร์เซ็นต์ค่าธรรมเนียมสินค้า
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mb: 0.5,
                fontWeight: 400,
                whiteSpace: "pre-line",
                "&.MuiTypography-root": { fontSize: font.size.sm },
              }}
            >
              {`สามารถกำหนดรายละเอียดค่าธรรมเนียมตามสินค้าแต่ละประเภทได้ เช่น สินค้า A 10%, สินค้า B 5% หรือ หากค่าธรรมเนียมสินค้าแต่ละประเภทเหมือนกัน ไม่ต้องใส่รายละเอียด`}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <RadioGroup
              row
              aria-labelledby="multiple-radio-group-label"
              name="multiple-radio-group"
              value={contractPercent?.multiple_fee}
              onChange={(e) => {
                let newContractPercent = { ...contractPercent };
                let isMultiple = e.target.value === "true";

                if (isMultiple) {
                  newContractPercent = {
                    ...newContractPercent,
                    percent_fee: [
                      ...newContractPercent?.percent_fee,
                      { description: "", percent: "" },
                    ],
                  };
                } else if (!isMultiple) {
                  newContractPercent = {
                    ...newContractPercent,
                    percent_fee: [
                      { percent: newContractPercent?.percent_fee[0]?.percent },
                    ],
                  };
                }
                setContractPercent({
                  ...newContractPercent,
                  multiple_fee: isMultiple,
                });
              }}
            >
              <FormControlLabel
                value={true}
                sx={{
                  flex: "0 0 50%",
                  width: "50%",
                  m: 0,
                  "&.MuiRadio-root": {
                    width: "auto",
                  },
                }}
                control={
                  <Radio
                    sx={{
                      fontSize: font.size.md,
                      color: "var(--primary-color)",
                      "&.Mui-checked": {
                        color: "var(--primary-color)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 22,
                      },
                    }}
                  />
                }
                label={"แยกตามประเภทสินค้า"}
              />

              <FormControlLabel
                value={false}
                sx={{
                  flex: "0 0 50%",
                  width: "50%",
                  m: 0,
                  "&.MuiRadio-root": {
                    width: "auto",
                  },
                }}
                control={
                  <Radio
                    sx={{
                      fontSize: font.size.md,
                      color: "var(--primary-color)",
                      "&.Mui-checked": {
                        color: "var(--primary-color)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 22,
                      },
                    }}
                  />
                }
                label={"เท่ากันทั้งหมด"}
              />
            </RadioGroup>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 4, backgroundColor: White01 }}>
              <Grid container columnSpacing={1} rowSpacing={2}>
                {contractPercent?.percent_fee &&
                  contractPercent?.percent_fee.map((fee, index) => (
                    <Grid
                      item
                      xs={contractPercent?.multiple_fee ? 6 : 12}
                      key={`item-${index}`}
                    >
                      {contractPercent?.multiple_fee ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            backgroundColor: White,
                            p: 1,
                            borderRadius: 4,
                          }}
                        >
                          <TextField
                            fullWidth
                            variant="outlined"
                            required
                            value={fee.description || ""}
                            onChange={(e) => {
                              const newPercentDesc = [
                                ...contractPercent.percent_fee,
                              ];
                              newPercentDesc[index] = {
                                ...newPercentDesc[index],
                                description: e.target.value,
                              };
                              setContractPercent({
                                ...contractPercent,
                                percent_fee: newPercentDesc,
                              });
                            }}
                            placeholder="สินค้า A"
                            error={!fee.description}
                          />
                          <TextField
                            fullWidth
                            variant="outlined"
                            required
                            value={fee.percent}
                            onChange={(e) => {
                              const newPercentDesc = [
                                ...contractPercent.percent_fee,
                              ];
                              newPercentDesc[index] = {
                                ...newPercentDesc[index],
                                percent: e.target.value,
                              };
                              setContractPercent({
                                ...contractPercent,
                                percent_fee: newPercentDesc,
                              });
                            }}
                            placeholder="10%"
                            error={!fee.percent}
                          />

                          {contractPercent?.percent_fee.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => handlePercentFee("remove", index)}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          variant="outlined"
                          required
                          value={fee.percent}
                          onChange={(e) => {
                            const newPercent = contractPercent
                              ?.percent_fee[0] || {
                              percent: "",
                            };
                            newPercent.percent = e.target.value;
                            setContractPercent({
                              ...contractPercent,
                              percent_fee: [newPercent],
                            });
                          }}
                          placeholder="สินค้า A 10%"
                          error={!fee.percent}
                        />
                      )}
                    </Grid>
                  ))}

                {contractPercent?.multiple_fee ? (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontSize: font.size.sm, fontWeight: 400 }}
                    >
                      *สามารถเพิ่มรายละเอียดค่าธรรมเนียมได้สูงสุด 6 รายการ*
                    </Typography>

                    <Button
                      variant="secondary"
                      startIcon={<AddIcon />}
                      disabled={contractPercent?.percent_fee?.length >= 6}
                      onClick={() => handlePercentFee("add")}
                    >
                      เพิ่มรายละเอียดค่าธรรมเนียม
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle1">
              ตั้งค่าภาษาที่ใช้ในสัญญา (Contract Language)
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <ToggleButtonGroup
              value={contractPercent?.language || "th"}
              exclusive
              onChange={(_, value) => {
                setContractPercent({
                  ...contractPercent,
                  language: value,
                });
              }}
              aria-label="selected language"
              fullWidth
            >
              <ToggleButton value="th" aria-label="thai">
                ไทย
              </ToggleButton>
              <ToggleButton value="en" aria-label="english">
                English
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleOnClose}
          sx={{ minWidth: 100 }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ minWidth: 100 }}
          disabled={!isValid}
          onClick={() => {
            handleSubmit();
          }}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetTermContractFeeDialog;
